// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"

/** `null` (or the empty string) deletes the key from the URL. */
export type UrlParamEntries = Record<string, string | null>

/**
 * The one way to write search params (ADR-0006). Merges `entries` into the
 * current URL: foreign params survive, a `null`/empty value deletes its key
 * so the canonical link stays clean, and every write pushes a history entry
 * so it round-trips through back/forward. `useTableUrlState` routes its
 * sort/page/visibility writes through this; views route their filter writes
 * through it too — a filter write must null its page param, since page N of
 * the previous result set is meaningless against the new one.
 */
export function useUrlParamWriter(): (entries: UrlParamEntries) => void {
  const [searchParams, setSearchParams] = useSearchParams()

  return useCallback(
    (entries: UrlParamEntries) => {
      const next = new URLSearchParams(searchParams)
      for (const [key, value] of Object.entries(entries)) {
        if (value === null || value === "") next.delete(key)
        else next.set(key, value)
      }
      setSearchParams(next, { replace: false })
    },
    [searchParams, setSearchParams],
  )
}
